import { Icon } from '@/components/Icon';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { EnvChoice } from './runBuildModel';

/** The label row shared by the environment and platform pickers. */
export function RequiredLabel({ children }: { children: string }) {
  return (
    <Label className="text-[13px]">
      {children}{' '}
      <span className="text-primary" aria-hidden>
        *
      </span>
    </Label>
  );
}

interface EnvironmentPickerProps {
  environments: EnvChoice[];
  env: EnvChoice | undefined;
  error: string | undefined;
  onPick: (slug: string) => void;
}

/** Environment radio group: an environment the profile has no binding for is shown but disabled. */
export function EnvironmentPicker({ environments, env, error, onPick }: EnvironmentPickerProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <RequiredLabel>Target environment</RequiredLabel>
      <div role="radiogroup" aria-label="Environment" className="grid gap-1 rounded-lg border bg-secondary p-1" style={{ gridTemplateColumns: `repeat(${Math.max(1, environments.length)}, minmax(0, 1fr))` }}>
        {environments.map((e) => (
          <EnvironmentOption key={e.id} choice={e} active={env?.id === e.id} onPick={onPick} />
        ))}
      </div>
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
      {env?.isProtected ? <ProtectedNote patterns={env.allowedBranchPatterns} /> : null}
    </div>
  );
}

function EnvironmentOption({ choice: e, active, onPick }: { choice: EnvChoice; active: boolean; onPick: (slug: string) => void }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      disabled={!e.bound}
      data-testid={`run-build-env-${e.slug}`}
      title={e.bound ? e.name : `${e.name}: this profile has no binding for it`}
      onClick={() => onPick(e.slug)}
      className={cn(
        'flex h-9 min-w-0 items-center justify-center gap-1.5 rounded-md text-[13px] font-medium transition-colors duration-150 max-sm:h-11',
        active ? 'bg-card text-foreground shadow-card' : 'text-muted-foreground hover:text-foreground',
        !e.bound && 'cursor-not-allowed opacity-40',
      )}
    >
      <span aria-hidden className="size-1.5 shrink-0 rounded-full" style={{ background: e.color ?? `var(--env-${e.kind})` }} />
      <span className="truncate">{e.name}</span>
      {e.isProtected ? <Icon name="lock" size={13} className="text-subtle" /> : null}
    </button>
  );
}

function ProtectedNote({ patterns }: { patterns: string[] }) {
  return (
    <p className="text-xs text-subtle">
      Protected environment · branches{' '}
      {patterns.length
        ? patterns.map((p) => (
            <code key={p} className="font-mono">
              {p}{' '}
            </code>
          ))
        : 'restricted'}
      · approval may be required
    </p>
  );
}
